import React, { useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { getRageLevel, mergeRage, rageProfile } from '../../../shared/rules/barbarianRage.mjs';
import { rollSkillWithDiceBox } from '../attributes/Skills';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';

/**
 * Relentless Rage (Barbarian 11): the raging Barbarian dropped to 0 HP. The player rolls the Constitution save the
 * server set up (its DC rises by 5 each use until a rest); success puts them back on their feet at twice their level.
 */
export default function RelentlessRagePrompt({ form, characterId, diceColor, onCharacterChange }) {
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const pending = rageProfile(form).raw.relentless;
  const show = pending?.status === 'pending';
  const respond = async (pass) => {
    if (lock.current || !show) return;
    lock.current = true; setBusy(true);
    try {
      let dice;
      if (!pass) {
        const rolled = await rollSkillWithDiceBox(pending.modifier, { diceColor, rollMode: pending.rollMode, rollContext: 'saving-throw', character: form, source: 'Relentless Rage' });
        dice = rolled.rolledD20s;
      }
      const response = await apiFetch(`/characters/${encodeURIComponent(String(form?._id || characterId))}/barbarian/relentless`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pass ? { id: pending.id, pass: true } : { id: pending.id, dice }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'The save could not be saved.');
      onCharacterChange?.((previous) => ({ ...mergeRage(previous, result.rage), ...(result.success ? { tempHealth: result.tempHealth, deathState: result.deathState } : {}) }));
      if (result.declined) notify('Relentless Rage declined. Your Rage ends.', 'info');
      else notify(result.success ? `Relentless Rage: ${result.total} vs DC ${result.dc}. You stay on your feet with ${result.tempHealth} HP.` : `Relentless Rage: ${result.total} vs DC ${result.dc}. You fall, and your Rage ends.`, result.success ? 'success' : 'warning');
    } catch (error) { notify(error.message || 'The save could not be saved.', 'danger'); }
    finally { lock.current = false; setBusy(false); }
  };
  if (!show) return null;
  const bonus = Number(pending.modifier) || 0;
  return (
    <Modal centered show backdrop="static" keyboard={false} className="dnd-modal modern-modal" aria-label="Relentless Rage">
      <Modal.Header><Modal.Title>Relentless Rage</Modal.Title></Modal.Header>
      <Modal.Body>
        You dropped to 0 HP while raging. Make a DC {pending.dc} Constitution saving throw ({bonus >= 0 ? '+' : ''}{bonus}{pending.rollMode !== 'normal' ? `, ${pending.rollMode}` : ''}).
        On a success you stay up with {2 * getRageLevel(form)} HP and keep raging. Each use raises the DC by 5 until you rest.
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => respond(true)}>Don’t use it</Button>
        <Button disabled={busy} onClick={() => respond(false)}>{busy ? 'Rolling…' : 'Roll Constitution save'}</Button>
      </Modal.Footer>
    </Modal>
  );
}
